import {
  Multiselect,
  MultiselectProps,
  Select,
  SelectProps
} from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';
import { ItemAirline } from '../../resources/types';


// --- PROPS INTERFACES ---

// When single = true
interface SelectAirlineSingleProps {
  single: true; //just true
  value?: number; // Single ID
  width?: number;
  onSelect?: (selected: number | null) => void; // <--- CHANGED: Returns ID or null
  readonly?: boolean;
  tokenLimit?: number;
  hideTokens?: boolean;
  dataset?: ItemAirline[];
  filteringType?: 'auto' | 'none';
}

// When single = false (or undefined, as per union type)
interface SelectAirlineMultiProps {
  single?: false; // default is "false"
  value?: number[]; // Array of IDs
  width?: number;
  onSelect?: (selected: number[]) => void; // <--- CHANGED: Returns array of IDs
  readonly?: boolean;
  tokenLimit?: number;
  hideTokens?: boolean;
  dataset?: ItemAirline[];
  filteringType?: 'auto' | 'none';
}

export type SelectAirlineProps =
  | SelectAirlineSingleProps
  | SelectAirlineMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectAirline: React.FC<SelectAirlineProps> = (props) => {
  const {
    dataset = [],
    single,
    value,
    width = 200,
    readonly,
    tokenLimit = 3,
    hideTokens = false,
    filteringType = 'none',
    onSelect
  } = props;

  const { data } = dataset.length > 0 ? { data: { airlines: dataset } } : useGlobalState();
  if (!data || !data.airlines) return <></>;

  // State for Single Selection (holds the selected object)
  const [selectedSingle, setSelectedSingle] = useState<ItemAirline | null>(null);

  // State for Multi Selection (holds the array of selected objects)
  const [selectedMulti, setSelectedMulti] = useState<ItemAirline[]>([]);

  const mappedairlines: ItemAirline[] = useMemo(() => {
    const airlines = data.airlines.map((plan: ItemAirline) => ({
      idAirline: plan.idAirline,
      airline: plan.airline,
      value: plan.idAirline.toString(),
      label: plan.airline,
      iconUrl: `https://fids-bucket.s3.us-east-1.amazonaws.com/media/images/airlines/fids/${plan.idAirline}.png`
    }));

    return airlines.sort((a: any, b: any) => {
      // 1. Determine if A or B are selected
      // Handle both single (number) and multi (array) cases
      const isASelected = Array.isArray(value)
        ? value.includes(a.idAirline)
        : value === a.idAirline;

      const isBSelected = Array.isArray(value)
        ? value.includes(b.idAirline)
        : value === b.idAirline;

      // 2. Sort Logic: Selected items (true) come before non-selected (false)
      if (isASelected && !isBSelected) return -1;
      if (!isASelected && isBSelected) return 1;

      // 3. Alphabetical fallback: sort by label if both are selected or both are not
      return a.label.localeCompare(b.label);
    });
  }, [data.airlines, value]); // Added 'value' here so it re-sorts on change


  // --- Initialization & Value Sync Effect ---

  useEffect(() => {
    if (!value) {
      setSelectedSingle(null);
      setSelectedMulti([]);
      return;
    }

    if (single) {
      // SINGLE MODE: Value is a number (ID)
      const id = value as number;
      const match = mappedairlines.find(airline => airline.idAirline === id) || null;
      setSelectedSingle(match);

    } else {
      // MULTI MODE: Value is an array of numbers (IDs)
      const ids = value as number[];
      const matches = mappedairlines.filter(airline => ids.includes(airline.idAirline));
      setSelectedMulti(matches);
    }
  }, [value, single, mappedairlines]);


  // --- Single Select Handler ---
  const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
    const selectedOption = detail.selectedOption as ItemAirline | null;

    setSelectedSingle(selectedOption);

    if (onSelect && single) {
      const selectedId = selectedOption?.idAirline ?? null; // Extract ID
      // FIX: Assert the function signature directly to satisfy the compiler
      (onSelect as (selected: number | null) => void)(selectedId);
    }
  };

  // --- Multi Select Handler ---
  const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
    const list = detail.selectedOptions as ItemAirline[];

    // Use spread to ensure setting a new array reference
    setSelectedMulti([...list]);

    if (onSelect && !single) {
      const selectedIds = list.map(item => item.idAirline); // Extract array of IDs
      // FIX: Assert the function signature directly to satisfy the compiler
      (onSelect as (selected: number[]) => void)(selectedIds);
    }
  };

  // --- Rendering ---

  const placeholder = single ? "Seleccionar aerolinea" : "Seleccionar aerolineas";
  const wrapperStyle: React.CSSProperties = {
    width: width > 0 ? `${width}px` : '100%'
  };
  return (
    <div style={wrapperStyle}>
      {single ? (
        // SINGLE SELECTION
        <Select
          filteringType={filteringType}
          readOnly={readonly}
          placeholder={placeholder}
          selectedOption={selectedSingle}
          options={mappedairlines}
          onChange={handleSingleChange}
        />
      ) : (
        // MULTI SELECTION
        <Multiselect
          filteringType={filteringType}
          tokenLimit={tokenLimit}
          hideTokens={hideTokens}
          readOnly={readonly}
          placeholder={selectedMulti.length === 0 ? placeholder : `${selectedMulti.length}/${mappedairlines.length} seleccionadas`}
          selectedOptions={selectedMulti}
          options={mappedairlines}
          onChange={handleMultiChange}
        />
      )}

    </div>
  );
};

export default SelectAirline;